import React, { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { styled, Theme } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { HeaderButton, HeaderButtons } from "types/general.types"
import { ADD, CLOSE_FRIENDS, CREATE_ITEM, ITEM, VIEWER } from "constants/index"
import { ROLE_BASED_PERMISSIONS } from "constants/permissions"
import { CUSTOM_BACK_BUTTON_CLICKED, NAVIGATE_TO_CREATE_ITEM_PAGE } from "constants/events"
import { shouldRenderUsername } from "utils/componentsUtils"
import { isItemViewPage } from "utils/navigatorUtils"
import { useAppSelector } from "appRedux/hooks"
import { resetWishListSlice } from "appRedux/slices/wishListSlice"
import { useAuth } from "context/AuthContext"
import ArrowIcon from "components/atoms/_icons/ArrowIcon"
import IconWrapper from "components/atoms/IconWrapper"
import { AddWishItemIconContainer } from "components/molecules/AddWishItemIconContainer"
import withRoleBasedPermission from "components/HOCs/withRoleBasedPermission"
import withPremiumPermission from "components/HOCs/withPremiumPermission"

type PageTitleProps = {
  title?: string;
  hasBackButton?: boolean;
  customBackButtonEvent?: string;
  headerButtons?: HeaderButtons | null;
  showInNavigation?: boolean;
}

const spacer = {
  key: "",
  eventName: "",
  icon: <></>
}

const IconWrapperWithRoleBasedPermission = withRoleBasedPermission(IconWrapper)
const IconWrapperWithPremiumPermission = withPremiumPermission(IconWrapperWithRoleBasedPermission)

export default function PageTitle(props: PageTitleProps) {
  const [title, setTitle] = useState<string>("")
  const [hasBackButton, setHasBackButton] = useState<boolean>(false)
  const [customBackButtonEvent, setCustomBackButtonEvent] = useState<string | null>(null)
  const [headerButtons, setHeaderButtons] = useState<HeaderButtons | null>(null)
  const [showInNavigation, setShowInNavigation] = useState<boolean>(false)

  const currentPageInfo = useAppSelector((state) => state.global.currentPageInfo)
  const role = useAppSelector((state) => state.global.role)
  const wishListViewMode = useAppSelector((state) => state.wishList.viewMode)
  const showCreateNewListOverlay = useAppSelector((state) => state.itemForm.showCreateNewListOverlay)
  const wishListState = useAppSelector((state) => state.wishList)

  const { user } = useAuth()

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const renderButton = useCallback(function (button: HeaderButton, index: number) {
    return (
      <IconWrapperWithPremiumPermission
        id={"with-permission"}
        key={index}
        resource={currentPageInfo?.key}
        action={button.key}
        feature={isAddItemIcon(button) ? CREATE_ITEM : ""}
        constraint={wishListState.listInfo?.privacy === CLOSE_FRIENDS && button.eventName === NAVIGATE_TO_CREATE_ITEM_PAGE}
        constraintType={CLOSE_FRIENDS}
        onClick={(event) => handleHeaderButtonClicked(event, button)}
      >
        {isAddItemIcon(button) ? <AddWishItemIconContainer>{button.icon}</AddWishItemIconContainer> : button.icon}
      </IconWrapperWithPremiumPermission>
    )
  }, [currentPageInfo])

  useEffect(() => {
    setTitle(props.title ? props.title : currentPageInfo?.title)
    setHasBackButton(props.hasBackButton ? props.hasBackButton : currentPageInfo?.hasBackButton)
    setCustomBackButtonEvent(props.customBackButtonEvent ? props.customBackButtonEvent : currentPageInfo?.customBackButtonEvent)
    setHeaderButtons(props.headerButtons ? props.headerButtons : currentPageInfo?.headerButtons)
    setShowInNavigation(props.showInNavigation ? props.showInNavigation : currentPageInfo?.showInNavigation)
  }, [currentPageInfo, props])

  function isAddItemIcon(button: HeaderButton) {
    return currentPageInfo && button.key === ADD
  }

  function handleHeaderButtonClicked(event: React.MouseEvent<HTMLDivElement>, button: HeaderButton) {
    document.dispatchEvent(new CustomEvent(button.eventName))
  }

  function handleBackButtonClicked() {
    if (customBackButtonEvent) {
      document.dispatchEvent(new CustomEvent(customBackButtonEvent))
      document.dispatchEvent(new CustomEvent(CUSTOM_BACK_BUTTON_CLICKED))
    } else {
      handleDefaultBackButton()
    }
  }

  function handleDefaultBackButton() {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate("/feed")
    }
    if (isItemViewPage(currentPageInfo?.path)) {
      setTimeout(() => {
        dispatch(resetWishListSlice())
      }, 10)
    }
  }

  function renderHeaderButtons(buttons: HeaderButton[]) {
    return (
      buttons.map((button, index) => (
        renderButton(button, index)
      ))
    )
  }

  function renderRightHeaderButtons() {
    const buttons: HeaderButton[] = []

    const leftButtonsWithRoleBasedPermissionLength = getButtonsLengthWithRoleBasedPermissionApplied(headerButtons?.left)
    const rightButtonsWithRoleBasedPermissionLength = getButtonsLengthWithRoleBasedPermissionApplied(headerButtons?.right)
    const leftButtonsLength = leftButtonsWithRoleBasedPermissionLength + (hasBackButton ? 1 : 0)
    const rightButtonsLength = rightButtonsWithRoleBasedPermissionLength

    if (rightButtonsLength < leftButtonsLength) {
      for (let i = 0; i < leftButtonsLength - rightButtonsLength; i++) {
        buttons.push(spacer)
      }
    }

    return (
      <HeaderRightButtonContainer
        istransparent={(isItemViewPage(currentPageInfo?.path) && wishListViewMode === ITEM) ? 1 : 0}
      >
        {headerButtons?.right && renderHeaderButtons(headerButtons?.right)}
        {renderHeaderButtons(buttons)}
      </HeaderRightButtonContainer>
    )
  }

  function renderLeftHeaderButtons() {
    const buttons = []

    const leftButtonsWithRoleBasedPermissionLength = getButtonsLengthWithRoleBasedPermissionApplied(headerButtons?.left)
    const rightButtonsWithRoleBasedPermissionLength = getButtonsLengthWithRoleBasedPermissionApplied(headerButtons?.right)
    const leftButtonsLength = leftButtonsWithRoleBasedPermissionLength + (hasBackButton ? 1 : 0)
    const rightButtonsLength = rightButtonsWithRoleBasedPermissionLength || 0

    if (leftButtonsLength < rightButtonsLength) {
      for (let i = 0; i < rightButtonsLength - leftButtonsLength; i++) {
        buttons.push(spacer)
      }
    }

    return (
      <HeaderLeftButtonContainer
        istransparent={(isItemViewPage(currentPageInfo?.path) && wishListViewMode === ITEM) ? 1 : 0}
      >
        {headerButtons?.left && renderHeaderButtons(headerButtons.left)}
        {renderHeaderButtons(buttons)}
      </HeaderLeftButtonContainer>
    )
  }

  function getButtonsLengthWithRoleBasedPermissionApplied(buttons?: HeaderButton[] | null) {
    if (!buttons) return 0
    let buttonsWithRoleBasedPermissionLength = 0
    for (const button of buttons) {
      const rolePermission = ROLE_BASED_PERMISSIONS[role as keyof typeof ROLE_BASED_PERMISSIONS]
      const pagePermission = rolePermission?.[currentPageInfo.key as keyof typeof rolePermission]
      const hasActionButtonPermission = pagePermission ? pagePermission?.[button.key as keyof typeof pagePermission] : true
      if (hasActionButtonPermission) {
        buttonsWithRoleBasedPermissionLength += 1
      }
    }
    return buttonsWithRoleBasedPermissionLength
  }

  function renderBackButton() {
    return (
      (hasBackButton && (!showInNavigation || role === VIEWER)) && <HeaderLeftButtonContainer
          istransparent={(isItemViewPage(currentPageInfo?.path) && wishListViewMode === ITEM && !showCreateNewListOverlay) ? 1 : 0}
          onClick={handleBackButtonClicked}>
          <IconWrapper>
              <ArrowIcon/>
          </IconWrapper>
      </HeaderLeftButtonContainer>
    )
  }

  function renderMiddleSection() {
    return (
      <TitleContainer>
        {title && <Title istransparent={isTransparent()}>{title}</Title>}
        {shouldRenderUsername(currentPageInfo, user) &&
            <Username istransparent={isTransparent()} size={title ? "0.9rem" : "1.3rem"}>
              {currentPageInfo?.dynamicPathParts?.handle}@
            </Username>}
      </TitleContainer>
    )
  }

  function shouldRenderPageTitle() {
    return !currentPageInfo?.showInNavigation
  }

  function isTransparent() {
    if (showCreateNewListOverlay) {
      return 0
    } else if (isItemViewPage(currentPageInfo?.path) && wishListViewMode === ITEM) {
      return 1
    } else if (title) {
      return 0
    } else {
      return 1
    }
  }

  return (
    shouldRenderPageTitle() ? <PageTitleContainer id="page-title" istransparent={isTransparent()}>
      {renderRightHeaderButtons()}
      {renderMiddleSection()}
      {renderLeftHeaderButtons()}
      {renderBackButton()}
    </PageTitleContainer> : null
  )
}

interface PageTitleContainerProps {
  theme?: Theme;
  istransparent: 1 | 0;
}

const PageTitleContainer = styled(Stack)(({ theme, istransparent }: PageTitleContainerProps) => ({
  width: "100%",
  maxWidth: "var(--max-container-width)",
  height: "48px",
  position: istransparent ? "relative" : "fixed",
  top: 0,
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  flexShrink: 0,
  backgroundColor: istransparent ? "transparent" : "#FFFFFF",
  borderBottom: "1px solid",
  borderBottomColor: istransparent ? "transparent" : theme?.palette.gray["4"],
  zIndex: 4
}))

const TitleContainer = styled(Stack)(({ theme }) => ({
  width: "100%",
  height: "100%",
  justifyContent: "center",
  alignItems: "center"
}))

const Title = styled(Typography)(({ theme, istransparent }: PageTitleContainerProps) => ({
  fontSize: "1.6rem",
  color: istransparent ? "#FFFFFF" : theme?.palette.gray["14"],
  fontWeight: 500,
  textAlign: "center",
  width: "70%",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis"
}))

interface UsernameProps extends PageTitleContainerProps {
  size: string;
}

const Username = styled(Typography)(({ theme, istransparent, size }: UsernameProps) => ({
  color: istransparent ? "#FFFFFF" : theme?.palette.gray["13"],
  fontSize: size || "0.9rem"
}))

const HeaderRightButtonContainer = styled(Stack)(({ theme, istransparent }: PageTitleContainerProps) => ({
  flexDirection: "row",
  zIndex: 10,

  "& svg": {
    stroke: istransparent ? "#FFFFFF" : theme?.palette.gray["13"]
  }
}))

const HeaderLeftButtonContainer = styled("div")(({ theme, istransparent }: PageTitleContainerProps) => ({
  zIndex: 10,

  "& svg": {
    stroke: istransparent ? "#FFFFFF" : theme?.palette.gray["13"],
    width: 24,
    height: 24
  }
}))
